import type { ReactNode } from "react";
import type { Header } from "@components/DataTable/CustomDataTable.types";
import { VCCertifcateTypes, type VCCertificateType } from "@constants/certificates";
import type {
  VesselCallNotificationDTO,
  VesselCallPortCall,
  VesselInformationDTO,
} from "@features/vessel-call/vessel-call-status/VesselCallStatus.types.tsx";
import type { Company } from "@services/vessel-call/types";
import type { Vessel, VesselCertificate } from "@services/vessel/types";
import type {
  CrewMember,
  GeneralDeclaration,
  PassengerEntry,
  VesselCallResponse,
} from "@services/vesselCall/types";
import type { VesselRegisterDetailsFixed } from "@services/vesselCall/types.tsx";
import type { TFunction } from "i18next";
import { t } from "i18next";

type SectionItem = {
  testId: string;
  translate: string;
  text?: ReactNode;
  textStyles?: string;
  wrapperStyle?: string;
  tranStyle?: string;
};

type NormalizedCrewMember = CrewMember & {
  placeOfBirth: string;
  dateOfBirth: string;
  expiryDate: string;
  passportNumber: string;
  issuingState?: string;
};

type NormalizedPassenger = PassengerEntry & {
  dateOfBirth: string;
  placeOfBirth: string;
  typeOfIdentityDocument?: string;
  identityDocumentNumber: string;
  issuingState?: string;
  expiryDate: string;
  visaNumberIfAppropriate: string;
  portOfEmbarkation?: string;
  portOfDisembarkation?: string;
  transitPassenger: boolean;
};

const NATURE_OF_CALL_TRANSLATION_KEY_MAP = {
  bunkering: "bunkering",
  cargoOperation: "cargo",
  crewChange: "crew",
  hullCleaning: "hull-cleaning",
  others: "other",
  projects: "projects",
  repairs: "repairs",
  storesProvision: "store-provision",
  sts: "sts",
  survey: "survey",
  underwaterInspection: "underwater-inspection",
  waitingOrders: "waiting-orders",
  wasteDisposal: "waste-disposal",
} as const;

type NatureOfCallLabelKey = keyof typeof NATURE_OF_CALL_TRANSLATION_KEY_MAP;

function isNatureOfCallLabelKey(value: string): value is NatureOfCallLabelKey {
  return value in NATURE_OF_CALL_TRANSLATION_KEY_MAP;
}

export function buildVesselInformationDetails(
  vesselRegisterDetailsFixed?: VesselRegisterDetailsFixed | null,
): VesselInformationDTO {
  const dashIfEmpty = (value: string | null | undefined): string => {
    const s = value?.trim();
    return s ? s : "-";
  };

  const deliveryYear = (() => {
    const dateOfDelivery = vesselRegisterDetailsFixed?.dateOfDelivery;
    if (!dateOfDelivery) return undefined;
    const ms = Date.parse(dateOfDelivery);
    if (Number.isNaN(ms)) return undefined;
    return new Date(ms).getUTCFullYear();
  })();

  return {
    imoNumber: vesselRegisterDetailsFixed?.vesselImoNumber,
    vesselName: dashIfEmpty(vesselRegisterDetailsFixed?.vesselName),
    vesselCallSign: dashIfEmpty(vesselRegisterDetailsFixed?.vesselCallSign),
    vesselType: dashIfEmpty(
      vesselRegisterDetailsFixed?.vesselTypeDesc ?? vesselRegisterDetailsFixed?.vesselTypeCode,
    ),
    grt: vesselRegisterDetailsFixed?.grt,
    shipBuildYear: vesselRegisterDetailsFixed?.shipBuildYear ?? deliveryYear,
    vesselTypeDesc: dashIfEmpty(
      vesselRegisterDetailsFixed?.vesselTypeDesc ?? vesselRegisterDetailsFixed?.vesselTypeCode,
    ),
    portCode: dashIfEmpty(vesselRegisterDetailsFixed?.portCode),
    portName: dashIfEmpty(vesselRegisterDetailsFixed?.portName),
    countryCode: dashIfEmpty(vesselRegisterDetailsFixed?.countryCode),
    nrt: vesselRegisterDetailsFixed?.nrt,
    dwt: vesselRegisterDetailsFixed?.dwt,
    loa: vesselRegisterDetailsFixed?.loa,
    beam: vesselRegisterDetailsFixed?.beam,
    classDesc: "-", // "no info currently on the Registration form, leave blank", requested from JIRA
    draftMax: vesselRegisterDetailsFixed?.draftMax,
    shipsOwner: dashIfEmpty(vesselRegisterDetailsFixed?.shipsOwner),
    shipsCharterer: dashIfEmpty(vesselRegisterDetailsFixed?.shipsCharterer),
    shipsManagement: dashIfEmpty(vesselRegisterDetailsFixed?.shipsManagement),
    shipsRegisteredOwner: dashIfEmpty(vesselRegisterDetailsFixed?.shipsRegisteredOwner),
    piClubName: dashIfEmpty(vesselRegisterDetailsFixed?.piClubName),
    mlcPiClubName: dashIfEmpty(vesselRegisterDetailsFixed?.mlcPiClubName),
  };
}
export function buildVesselCallNotificationDetails(
  vesselCallDetails: VesselCallResponse | null,
  companies: Company[],
): VesselCallNotificationDTO {
  const portCalls: VesselCallPortCall[] | undefined = vesselCallDetails?.portCalls?.map((pc) => ({
    port: pc.port ?? pc.portReferenceCode ?? "",
    country: pc.country ?? pc.countryReferenceCode ?? "",
    arrivalDate: pc.arrivalDate,
    departureDate: pc.departureDate,
    securityLevel: pc.securityLevel,
    internalNotes: pc.internalNotes,
  }));

  return {
    expectedArrivalLocation: vesselCallDetails?.expectedArrivalLocation,
    preferredAnchorageArea: vesselCallDetails?.preferredAnchorageArea?.anchorageAreaDescription,
    reasonForAnchoragePreference: vesselCallDetails?.reasonForAnchoragePreference,
    assignedAnchorageArea: vesselCallDetails?.assignedAnchorageArea?.anchorageAreaDescription,
    canOperateAsBunkerBarge: vesselCallDetails?.canOperateAsBunkerBarge,
    diplomaticVessel: vesselCallDetails?.diplomaticVessel,
    selectPort: vesselCallDetails?.selectPort,
    masterName: vesselCallDetails?.masterName,
    nationality: vesselCallDetails?.nationality,
    eta: vesselCallDetails?.eta,
    etd: vesselCallDetails?.etd,
    lastPort: vesselCallDetails?.lastPort,
    nextPort: vesselCallDetails?.nextPort,
    reEntryVessel: vesselCallDetails?.reEntryVessel,
    overstayChargesApplicable: vesselCallDetails?.overstayChargesApplicable,
    draftFwd: vesselCallDetails?.draftFwd,
    draftAft: vesselCallDetails?.draftAft,
    ownersMattersAgent: capitalizeFunction(
      companies.find((c) => c.companyCode === vesselCallDetails?.ownersMattersAgent)
        ?.companyNameEnglish,
    ),
    vesselCondition: vesselCallDetails?.vesselCondition,
    cargoDescription: vesselCallDetails?.cargoDescription,
    cargoOnboard: vesselCallDetails?.cargoOnboard,
    cargoDangerous: vesselCallDetails?.cargoDangerous,
    cargoQuantity: vesselCallDetails?.cargoQuantity,
    cargoImoHazardClasses: vesselCallDetails?.cargoImoHazardClasses,
    natureOfCall: vesselCallDetails?.natureOfCall,
    unitOfMeasurement: vesselCallDetails?.cargoMeasurementUnit,
    imo2020Compliance: vesselCallDetails?.imo2020Compliance,
    portFacility: vesselCallDetails?.portFacility,
    portFacilitySpecify: vesselCallDetails?.portFacilitySpecify,
    securityLevel: vesselCallDetails?.securityLevel,
    shipPortSecurityMeasuresTaken: vesselCallDetails?.shipPortSecurityMeasuresTaken,
    isscValid: vesselCallDetails?.isscValid,
    isscIssueDate: vesselCallDetails?.isscIssueDate,
    isscExpiryDate: vesselCallDetails?.isscExpiryDate,
    takenMeasures: vesselCallDetails?.takenMeasures,
    isscNumber: vesselCallDetails?.isscNumber,
    securityGuardsOnBoard: vesselCallDetails?.securityGuardsOnBoard,
    noOfGuards: vesselCallDetails?.securityGuardsNumber,
    armAmmunitionOnBoard: vesselCallDetails?.armAmmunitionOnboard,
    isscIssuedBy: vesselCallDetails?.isscIssuedBy,
    vesselHasDeficiencies: vesselCallDetails?.vesselHasDeficiencies,
    deficiencies: vesselCallDetails?.deficiencies,
    sspOnboardApproved: vesselCallDetails?.sspOnboardApproved,
    portCalls,
    armAmmunitionProofDocumentUrl: vesselCallDetails?.armAmmunitionProofDocumentUrl,
    deficienciesDocumentsUrl: vesselCallDetails?.deficienciesDocumentsUrl,
    flagStatePermissionDocumentUrl: vesselCallDetails?.flagStatePermissionDocumentUrl,
    lastPortClearanceDocumentUrl: vesselCallDetails?.lastPortClearanceDocumentUrl,
    lastPortClearanceDocumentName: vesselCallDetails?.lastPortClearanceDocumentName,
    lastPortClearanceClassName: vesselCallDetails?.lastPortClearanceClassName,
  };
}
function getFileExtensionFromUrl(url?: string | null): string {
  return url?.split(".").pop() || "";
}
export function getPreArrivalCertificates(
  vesselDetails?: VesselCallNotificationDTO | null,
): VesselCertificate[] {
  const flagExt = getFileExtensionFromUrl(vesselDetails?.flagStatePermissionDocumentUrl);
  const ammoExt = getFileExtensionFromUrl(vesselDetails?.armAmmunitionProofDocumentUrl);
  const lastExt = getFileExtensionFromUrl(vesselDetails?.lastPortClearanceDocumentUrl);
  const defExt = getFileExtensionFromUrl(vesselDetails?.deficienciesDocumentsUrl);
  return [
    vesselDetails?.flagStatePermissionDocumentUrl && {
      certificateUrl: vesselDetails.flagStatePermissionDocumentUrl,
      certificateDocumentName: `${t("vessel-call.PreArrivalNotification.flag-state-certificate-name")}.${flagExt}`,
      certificateType: VCCertifcateTypes.FLAG_STATE_PERMISSION as VCCertificateType,
      certificateClassName: "",
      certificateExpiryDate: "",
    },
    vesselDetails?.armAmmunitionProofDocumentUrl && {
      certificateUrl: vesselDetails.armAmmunitionProofDocumentUrl,
      certificateDocumentName: `${t(
        "vessel-call.PreArrivalNotification.ammunition-proof-certificate-name",
      )}.${ammoExt}`,
      certificateType: VCCertifcateTypes.ARM_AMMUNITION_CERTIFICATE as VCCertificateType,
      certificateClassName: "",
      certificateExpiryDate: "",
    },
    vesselDetails?.lastPortClearanceDocumentUrl && {
      certificateUrl: vesselDetails.lastPortClearanceDocumentUrl,
      certificateDocumentName:
        vesselDetails.lastPortClearanceDocumentName ||
        `${t("vessel-call.PreArrivalNotification.last-port-clearance-certificate-name")}.${lastExt}`,
      certificateType: VCCertifcateTypes.LAST_PORT_CLEARANCE as VCCertificateType,
      certificateClassName: vesselDetails.lastPortClearanceClassName,
      certificateExpiryDate: "",
    },
    vesselDetails?.deficienciesDocumentsUrl && {
      certificateUrl: vesselDetails.deficienciesDocumentsUrl,
      certificateDocumentName: `${t(
        "vessel-call.PreArrivalNotification.deficiencies-docs-certificate-name",
      )}.${defExt}`,
      certificateType: VCCertifcateTypes.DEFICIENCIES_DOCUMENTS as VCCertificateType,
      certificateClassName: "",
      certificateExpiryDate: "",
    },
  ].filter(Boolean) as VesselCertificate[];
}
export const formatDateToDDMMYYYY = (isoDate: string): string => {
  const [year, month, day] = isoDate.split("-");
  return `${day}/${month}/${year}`;
};

export function capitalizeFunction(text?: string): string {
  if (!text) return "-";
  const lowered = text.toLowerCase();
  return lowered.charAt(0).toUpperCase() + lowered.slice(1);
}

export function formatDateTime(dateString?: string | null): string {
  if (!dateString) return "-";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  const yyyy = date.getFullYear();
  const hh = String(date.getHours()).padStart(2, "0");
  const min = String(date.getMinutes()).padStart(2, "0");
  return `${dd}/${mm}/${yyyy}\n${hh}:${min}`;
}

function formatOptionalBoolean(value: boolean | undefined, t: TFunction): string {
  if (value === true) return t("common.yes");
  if (value === false) return t("common.no");
  return "-";
}

export function formatDate(dateString?: string | null): string {
  if (!dateString) return "-";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  const yyyy = date.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

export function getPortSubSectionItems({
  vesselDetails,
  TEST_ID_BASE,
  LANG_BASE,
  t,
  transStyleClass,
  wrapperStyleClass,
  wrapperFirstStyleClass,
}: {
  vesselDetails?: VesselCallNotificationDTO | null;
  TEST_ID_BASE: string;
  LANG_BASE: string;
  t: TFunction;
  transStyleClass: string;
  wrapperStyleClass: string;
  wrapperFirstStyleClass: string;
}) {
  const getAnchorageAreaRows = () => {
    if (["FOAA", "DOAA"].includes(vesselDetails?.expectedArrivalLocation ?? "")) {
      const rows = [
        [
          {
            testId: `${TEST_ID_BASE}-preferred-anchorage-area`,
            translate: t(`${LANG_BASE}.PreArrivalNotification.preferred-anchorage-area`),
            text: vesselDetails?.preferredAnchorageArea || "-",
            textStyles: "font-semibold",
            tranStyle: transStyleClass,
            wrapperStyle: wrapperStyleClass,
          },
          {
            testId: `${TEST_ID_BASE}-reason-for-anchorage-preference`,
            translate: t(`${LANG_BASE}.PreArrivalNotification.reason-for-anchorage-preference`),
            text: vesselDetails?.reasonForAnchoragePreference || "-",
            textStyles: "font-semibold",
            tranStyle: transStyleClass,
            wrapperStyle: wrapperStyleClass,
          },
        ],
      ];

      if (vesselDetails?.assignedAnchorageArea) {
        rows.push([
          {
            testId: `${TEST_ID_BASE}-assigned-anchorage-area`,
            translate: t(`${LANG_BASE}.PreArrivalNotification.assigned-anchorage-area`),
            text: vesselDetails?.assignedAnchorageArea || "-",
            textStyles: "font-semibold",
            tranStyle: transStyleClass,
            wrapperStyle: wrapperStyleClass,
          },
        ]);
      }

      return rows;
    }
    return [];
  };

  return [
    [
      {
        testId: `${TEST_ID_BASE}-port`,
        translate: t(`${LANG_BASE}.PreArrivalNotification.port`),
        text: vesselDetails?.selectPort || "-",
        textStyles: "font-semibold",
        tranStyle: transStyleClass,
        wrapperStyle: wrapperFirstStyleClass,
      },
      {
        testId: `${TEST_ID_BASE}-expected-arrival-location`,
        translate: t(`${LANG_BASE}.PreArrivalNotification.expected-arrival-location`),
        text: vesselDetails?.expectedArrivalLocation || "-",
        textStyles: "font-semibold",
        tranStyle: transStyleClass,
        wrapperStyle: wrapperFirstStyleClass,
      },
    ],
    ...getAnchorageAreaRows(),
  ];
}

export function getPrimaryNatureOfCallSubSectionItems({
  vesselDetails,
  TEST_ID_BASE,
  LANG_BASE,
  t,
  transStyleClass,
  wrapperFirstStyleClass,
}: {
  vesselDetails?: VesselCallNotificationDTO | null;
  TEST_ID_BASE: string;
  LANG_BASE: string;
  t: TFunction;
  transStyleClass: string;
  wrapperFirstStyleClass: string;
}) {
  return [
    {
      testId: `${TEST_ID_BASE}-primary-nature-of-call`,
      translate: t(`${LANG_BASE}.PreArrivalNotification.primary-nature-of-call`),
      text: vesselDetails?.natureOfCall?.primaryNatureOfCallDesc || "-",
      textStyles: "font-semibold",
      tranStyle: transStyleClass,
      wrapperStyle: wrapperFirstStyleClass,
    },
  ] satisfies SectionItem[];
}

export function getVoyageSubSectionItems({
  vesselDetails,
  TEST_ID_BASE,
  LANG_BASE,
  t,
  transStyleClass,
  wrapperStyleClass,
  wrapperFirstStyleClass,
}: {
  vesselDetails?: VesselCallNotificationDTO | null;
  TEST_ID_BASE: string;
  LANG_BASE: string;
  t: TFunction;
  transStyleClass: string;
  wrapperStyleClass: string;
  wrapperFirstStyleClass: string;
}) {
  const marineConfirmationRow =
    vesselDetails?.reEntryVessel !== undefined ||
    vesselDetails?.overstayChargesApplicable !== undefined
      ? [
          {
            testId: `${TEST_ID_BASE}-reEntryVessel`,
            translate: t(`${LANG_BASE}.PreArrivalNotification.re-entry-vessel`),
            text: formatOptionalBoolean(vesselDetails?.reEntryVessel, t),
            textStyles: "font-semibold",
            tranStyle: transStyleClass,
            wrapperStyle: wrapperStyleClass,
          },
          {
            testId: `${TEST_ID_BASE}-overstayChargesApplicable`,
            translate: t(
              `${LANG_BASE}.PreArrivalNotification.overstay-charges-applicable-from-day-1`,
            ),
            text: formatOptionalBoolean(vesselDetails?.overstayChargesApplicable, t),
            textStyles: "font-semibold",
            tranStyle: transStyleClass,
            wrapperStyle: wrapperStyleClass,
          },
        ]
      : [];

  return [
    [
      {
        testId: `${TEST_ID_BASE}-masterName`,
        translate: t(`${LANG_BASE}.PreArrivalNotification.master's-name`),
        text: vesselDetails?.masterName || "-",
        textStyles: "font-semibold",
        tranStyle: transStyleClass,
        wrapperStyle: wrapperFirstStyleClass,
      },
      {
        testId: `${TEST_ID_BASE}-nationality`,
        translate: t(`${LANG_BASE}.PreArrivalNotification.nationality`),
        text: vesselDetails?.nationality || "-",
        textStyles: "font-semibold",
        tranStyle: transStyleClass,
        wrapperStyle: wrapperFirstStyleClass,
      },
    ],
    [
      {
        testId: `${TEST_ID_BASE}-eta`,
        translate: t(`${LANG_BASE}.PreArrivalNotification.eta`),
        text: formatDateTime(vesselDetails?.eta),
        textStyles: "font-semibold",
        tranStyle: transStyleClass,
        wrapperStyle: wrapperStyleClass,
      },
      {
        testId: `${TEST_ID_BASE}-etd`,
        translate: t(`${LANG_BASE}.PreArrivalNotification.etd`),
        text: formatDateTime(vesselDetails?.etd),
        textStyles: "font-semibold",
        tranStyle: transStyleClass,
        wrapperStyle: wrapperStyleClass,
      },
    ],
    [
      {
        testId: `${TEST_ID_BASE}-lastPort`,
        translate: t(`${LANG_BASE}.PreArrivalNotification.last-port`),
        text: vesselDetails?.lastPort || "-",
        textStyles: "font-semibold",
        tranStyle: transStyleClass,
        wrapperStyle: wrapperStyleClass,
      },
      {
        testId: `${TEST_ID_BASE}-nextPort`,
        translate: t(`${LANG_BASE}.PreArrivalNotification.next-port`),
        text: vesselDetails?.nextPort || "-",
        textStyles: "font-semibold",
        tranStyle: transStyleClass,
        wrapperStyle: wrapperStyleClass,
      },
    ],
    ...(marineConfirmationRow.length ? [marineConfirmationRow] : []),
    [
      {
        testId: `${TEST_ID_BASE}-draftFwd`,
        translate: t(`${LANG_BASE}.PreArrivalNotification.draft-fwd`),
        text:
          vesselDetails?.draftFwd !== undefined &&
          vesselDetails?.draftFwd !== null &&
          vesselDetails.draftFwd !== 0
            ? `${vesselDetails.draftFwd} m`
            : "-",
        textStyles: "font-semibold",
        tranStyle: transStyleClass,
        wrapperStyle: wrapperStyleClass,
      },
      {
        testId: `${TEST_ID_BASE}-draftAft`,
        translate: t(`${LANG_BASE}.PreArrivalNotification.draft-aft`),
        text:
          vesselDetails?.draftAft !== undefined &&
          vesselDetails?.draftAft !== null &&
          vesselDetails.draftAft !== 0
            ? `${vesselDetails.draftAft} m`
            : "-",
        textStyles: "font-semibold",
        tranStyle: transStyleClass,
        wrapperStyle: wrapperStyleClass,
      },
    ],
    [
      {
        testId: `${TEST_ID_BASE}-ownersMattersAgent`,
        translate: t(`${LANG_BASE}.PreArrivalNotification.owner's-matters-agent`),
        text: vesselDetails?.ownersMattersAgent || "-",
        textStyles: "font-semibold",
        tranStyle: transStyleClass,
        wrapperStyle: wrapperStyleClass,
      },
      {
        testId: `${TEST_ID_BASE}-vesselCondition`,
        translate: t(`${LANG_BASE}.PreArrivalNotification.vessel-condition`),
        text: vesselDetails?.vesselCondition || "-",
        textStyles: "font-semibold",
        tranStyle: transStyleClass,
        wrapperStyle: wrapperStyleClass,
      },
    ],
  ];
}

export function getCargoOnboardSubSectionItems({
  vesselDetails,
  TEST_ID_BASE,
  LANG_BASE,
  t,
  transStyleClass,
  wrapperStyleClass,
  wrapperFirstStyleClass,
}: {
  vesselDetails?: VesselCallNotificationDTO | null;
  TEST_ID_BASE: string;
  LANG_BASE: string;
  t: TFunction;
  transStyleClass: string;
  wrapperStyleClass: string;
  wrapperFirstStyleClass: string;
}) {
  const left = [
    {
      testId: `${TEST_ID_BASE}-desc`,
      translate: t(`${LANG_BASE}.PreArrivalNotification.description`),
      text: vesselDetails?.cargoDescription || "-",
      textStyles: "font-semibold",
      wrapperStyle: wrapperFirstStyleClass,
      tranStyle: transStyleClass,
    },
    {
      testId: `${TEST_ID_BASE}-qty`,
      translate: t(`${LANG_BASE}.PreArrivalNotification.quantity`),
      text: vesselDetails?.cargoQuantity || "-",
      textStyles: "font-semibold",
      wrapperStyle: wrapperStyleClass,
      tranStyle: transStyleClass,
    },
    {
      testId: `${TEST_ID_BASE}-unit`,
      translate: t(`${LANG_BASE}.PreArrivalNotification.unit-of-measurement`),
      text: vesselDetails?.unitOfMeasurement || "-",
      textStyles: "font-semibold",
      wrapperStyle: wrapperStyleClass,
      tranStyle: transStyleClass,
    },
  ];
  const right = [
    {
      testId: `${TEST_ID_BASE}-hazard`,
      translate: t(`${LANG_BASE}.PreArrivalNotification.hazardous-and-dangerous-cargo?`),
      text:
        vesselDetails?.cargoDangerous === true
          ? t("common.yes")
          : vesselDetails?.cargoDangerous === false
            ? t("common.no")
            : "-",
      textStyles: "font-semibold",
      wrapperStyle: wrapperFirstStyleClass,
      tranStyle: transStyleClass,
    },
  ];
  if (vesselDetails?.cargoDangerous === true) {
    right.push({
      testId: `${TEST_ID_BASE}-imo-hazard`,
      translate: t(`${LANG_BASE}.PreArrivalNotification.imo-hazard-classes`),
      text: vesselDetails.cargoImoHazardClasses || "-",
      textStyles: "font-semibold",
      wrapperStyle: wrapperStyleClass,
      tranStyle: transStyleClass,
    });
  }

  return [left, right];
}

export function getFalDocumentFileName(
  url: string | null | undefined,
  base: string,
  imo?: string | number | null,
): string {
  if (!url) return base;
  const clean = url.split(/[?#]/)[0];
  const lastSegment = clean.split("/").filter(Boolean).pop() || `${base}.pdf`;
  const ext = lastSegment.includes(".") ? lastSegment.split(".").pop() || "pdf" : "pdf";
  const imoStr = imo !== null && imo !== undefined ? String(imo) : "";
  return imoStr ? `${base}-${imoStr}.${ext}` : `${base}.${ext}`;
}

export function getFal1GeneralDeclarationData({
  vesselCallDetails,
  t,
}: {
  vesselCallDetails: VesselCallResponse | null;
  t: TFunction;
}) {
  const gd: Partial<GeneralDeclaration> = vesselCallDetails?.generalDeclaration || {};

  const shipNameAndType = gd.nameAndTypeOfShip || "-";
  const signedDocUrl = gd.completedAndSignedDocumentUrl || "";
  const signedDocName = getFalDocumentFileName(
    signedDocUrl,
    "completedAndSignedForm",
    gd.imoNumber,
  );

  return {
    testIds: {
      wrapper: "review-fal-1",
      grid: "fal-form-1-grid",
      left: "fal-form-1-left",
      right: "fal-form-1-right",
      signedCard: "fal-form-1-completed-signed-form-card",
      signedEmpty: "fal-form-1-signed-form-empty",
    },
    translationTitle: t("vessel-call.falform1.title"),
    shipNameAndType,
    fieldsLeft: [
      {
        testId: "fal-form-1-name-and-type-of-ship",
        translate: t("vessel-call.falform1.name-and-type-of-ship"),
        value: shipNameAndType,
      },
      {
        testId: "fal-form-1-imo-number",
        translate: t("vessel-call.falform1.imo-number"),
        value: gd.imoNumber !== undefined && gd.imoNumber !== null ? String(gd.imoNumber) : "-",
      },
      {
        testId: "fal-form-1-call-sign",
        translate: t("vessel-call.falform1.call-sign"),
        value: gd.callSign || "-",
      },
      {
        testId: "fal-form-1-voyage-number",
        translate: t("vessel-call.falform1.voyage-number"),
        value: gd.voyageNumber || "-",
      },
      {
        testId: "fal-form-1-port-of-arrival",
        translate: t("vessel-call.falform1.port-of-arrival"),
        value: gd.arrivalPort || "-",
      },
      {
        testId: "fal-form-1-date-time-of-arrival",
        translate: t("vessel-call.falform1.date-time-of-arrival"),
        value: formatEta(gd.arrivalDateTime),
      },
      {
        testId: "fal-form-1-flag-state-of-ship",
        translate: t("vessel-call.falform1.flag-state-of-ship"),
        value: gd.flagStateOfShip || "-",
      },
      {
        testId: "fal-form-1-name-of-master",
        translate: t("vessel-call.falform1.name-of-master"),
        value: gd.masterName || "-",
      },
      {
        testId: "fal-form-1-last-port-of-call",
        translate: t("vessel-call.falform1.last-port-of-call"),
        value: gd.lastPortOfCall || "-",
      },
      {
        testId: "fal-form-1-certificate-of-registry",
        translate: t("vessel-call.falform1.certificate-of-registry"),
        value: gd.registryCertificate || "-",
      },
    ],
    fieldsRight: [
      {
        testId: "fal-form-1-ship-agent-contact",
        translate: t("vessel-call.falform1.ship-agent-contact"),
        value: gd.shipAgentDetails || "-",
      },
      {
        testId: "fal-form-1-gross-tonnage",
        translate: t("vessel-call.falform1.gross-tonnage"),
        value:
          gd.grossTonnage !== undefined && gd.grossTonnage !== null ? String(gd.grossTonnage) : "-",
      },
      {
        testId: "fal-form-1-net-tonnage",
        translate: t("vessel-call.falform1.net-tonnage"),
        value: gd.netTonnage !== undefined && gd.netTonnage !== null ? String(gd.netTonnage) : "-",
      },
      {
        testId: "fal-form-1-voyage-details",
        translate: t("vessel-call.falform1.voyage-particulars-short"),
        value: gd.voyageDetails || "-",
      },
      {
        testId: "fal-form-1-cargo-description",
        translate: t("vessel-call.falform1.cargo-description"),
        value: gd.cargoDescription || "-",
      },
      {
        testId: "fal-form-1-crew-number",
        translate: t("vessel-call.falform1.number-of-crew"),
        value: gd.crewNumber !== undefined && gd.crewNumber !== null ? String(gd.crewNumber) : "-",
      },
      {
        testId: "fal-form-1-passengers-number",
        translate: t("vessel-call.falform1.number-of-passengers"),
        value:
          gd.passengersNumber !== undefined && gd.passengersNumber !== null
            ? String(gd.passengersNumber)
            : "-",
      },
      {
        testId: "fal-form-1-remarks",
        translate: t("vessel-call.falform1.remarks"),
        value: gd.remarks || "-",
      },
      {
        testId: "fal-form-1-waste-requirements",
        translate: t("vessel-call.falform1.ship-requirements"),
        value: gd.wasteShipRequirements || "-",
      },
    ],
    signedDocUrl,
    signedDocName,
    signedTitle: t("vessel-call.falform1.signed-form"),
  };
}

export function getFal2CargoDeclarationData({
  vesselCallDetails,
  vesselRegisterDetails,
  t,
}: {
  vesselCallDetails: VesselCallResponse | null;
  vesselRegisterDetails: Vessel | null;
  t: TFunction;
}) {
  const cargoUrl = vesselCallDetails?.cargoDeclarationDocumentUrl || "";
  const fileName = getFalDocumentFileName(
    cargoUrl,
    "cargoDeclaration",
    vesselRegisterDetails?.vesselImoNumber,
  );
  return {
    cargoUrl,
    fileName,
    translationTitle: t("vessel-call.certificates.CARGO_DECLARATION"),
    testIds: {
      wrapper: "review-fal-2",
      card: "review-fal-2-cargo-declaration-card",
      empty: "review-fal-2-no-cargo-doc",
    },
  };
}

export function getFal3ShipsStoresDeclarationData({
  vesselCallDetails,
  vesselRegisterDetails,
  t,
}: {
  vesselCallDetails: VesselCallResponse | null;
  vesselRegisterDetails: Vessel | null;
  t: TFunction;
}) {
  const shipsStoresUrl = vesselCallDetails?.shipsStoresDeclarationDocumentUrl || "";
  const fileName = getFalDocumentFileName(
    shipsStoresUrl,
    "shipsStoresDeclaration",
    vesselRegisterDetails?.vesselImoNumber,
  );
  return {
    shipsStoresUrl,
    fileName,
    translationTitle: t("vessel-call.certificates.SHIPS_STORES_DECLARATION"),
    testIds: {
      wrapper: "review-fal-3",
      card: "review-fal-3-ships-stores-declaration-card",
      empty: "review-fal-3-no-ships-stores-doc",
    },
  };
}

export function getFal4CrewsEffectsDeclarationData({
  vesselCallDetails,
  vesselRegisterDetails,
  t,
}: {
  vesselCallDetails: VesselCallResponse | null;
  vesselRegisterDetails: Vessel | null;
  t: TFunction;
}) {
  const crewsEffectsUrl = vesselCallDetails?.crewsEffectsDeclarationDocumentUrl || "";
  const fileName = getFalDocumentFileName(
    crewsEffectsUrl,
    "crewsEffectsDeclaration",
    vesselRegisterDetails?.vesselImoNumber,
  );
  return {
    crewsEffectsUrl,
    fileName,
    translationTitle: t("vessel-call.certificates.CREWS_EFFECTS_DECLARATION"),
    testIds: {
      wrapper: "review-fal-4",
      card: "review-fal-4-crews-effects-declaration-card",
      empty: "review-fal-4-no-crews-effects-doc",
    },
  };
}

export function getFal5CrewListData({
  vesselCallDetails,
  t,
}: {
  vesselCallDetails: VesselCallResponse | null;
  t: TFunction;
}) {
  const crew = vesselCallDetails?.crews || [];
  const headers: Header[] = [
    { field: "no", columnName: t("vessel-call.create.table-headers.no"), minWidth: 80, flex: 1 },
    {
      field: "familyName",
      columnName: t("vessel-call.create.table-headers.family-name"),
      minWidth: 120,
    },
    {
      field: "givenName",
      columnName: t("vessel-call.create.table-headers.given-name"),
      minWidth: 120,
    },
    {
      field: "rank",
      columnName: t("vessel-call.create.table-headers.rank"),
      minWidth: 160,
    },
    {
      field: "nationality",
      columnName: t("vessel-call.create.table-headers.nationality"),
      minWidth: 140,
    },
    {
      field: "dateOfBirth",
      columnName: t("vessel-call.create.table-headers.date-of-birth"),
      type: "date",
      minWidth: 140,
    },
    {
      field: "placeOfBirth",
      columnName: t("vessel-call.create.table-headers.place-of-birth"),
      minWidth: 160,
    },
    { field: "gender", columnName: t("vessel-call.create.table-headers.gender"), minWidth: 100 },
    {
      field: "passportNumber",
      columnName: t("vessel-call.create.table-headers.passport-number"),
      minWidth: 180,
    },
    {
      field: "issuingState",
      columnName: t("vessel-call.create.table-headers.passport-issuing-country"),
      minWidth: 160,
    },
    {
      field: "expiryDate",
      columnName: t("vessel-call.create.table-headers.passport-expiry-date"),
      type: "date",
      minWidth: 180,
    },
  ];
  const normalizedCrew = normalizeCrewMembers(crew);
  const rows = normalizedCrew
    .slice()
    .sort(
      (left, right) =>
        (left.no ?? Number.POSITIVE_INFINITY) - (right.no ?? Number.POSITIVE_INFINITY),
    )
    .map((c, idx) => ({
      ...c,
      id: `crew-${idx}`,
      no: typeof c.no === "number" && Number.isFinite(c.no) ? c.no : "",
    }));
  return {
    testIds: {
      wrapper: "review-fal-5",
      empty: "review-fal-5-empty",
    },
    translationTitle: t("vessel-call.create.fal-form-5-title"),
    headers,
    rows,
    total: crew.length,
  };
}

export function getFal6PassengerListData({
  vesselCallDetails,
  t,
}: {
  vesselCallDetails: VesselCallResponse | null;
  t: TFunction;
}) {
  const passengers = vesselCallDetails?.passengers || [];
  const headers: Header[] = [
    { field: "no", columnName: t("vessel-call.create.table-headers.no"), minWidth: 80, flex: 1 },
    {
      field: "familyName",
      columnName: t("vessel-call.create.table-headers.family-name"),
      minWidth: 120,
    },
    {
      field: "givenName",
      columnName: t("vessel-call.create.table-headers.given-name"),
      minWidth: 120,
    },
    {
      field: "nationality",
      columnName: t("vessel-call.create.table-headers.nationality"),
      minWidth: 140,
    },
    {
      field: "dateOfBirth",
      columnName: t("vessel-call.create.table-headers.date-of-birth"),
      type: "date",
      minWidth: 140,
    },
    {
      field: "placeOfBirth",
      columnName: t("vessel-call.create.table-headers.place-of-birth"),
      minWidth: 160,
    },
    { field: "gender", columnName: t("vessel-call.create.table-headers.gender"), minWidth: 80 },
    {
      field: "identityDocumentNumber",
      columnName: t("vessel-call.create.table-headers.passport-number"),
      minWidth: 160,
    },
    {
      field: "issuingState",
      columnName: t("vessel-call.create.table-headers.passport-issuing-country"),
      minWidth: 160,
    },
    {
      field: "expiryDate",
      columnName: t("vessel-call.create.table-headers.passport-expiry-date"),
      type: "date",
      minWidth: 180,
    },
    {
      field: "portOfEmbarkation",
      columnName: t("vessel-call.create.table-headers.port-of-embarkation"),
      minWidth: 140,
    },
    {
      field: "portOfDisembarkation",
      columnName: t("vessel-call.create.table-headers.port-of-disembarkation"),
      minWidth: 160,
    },
    {
      field: "transitPassengerLabel",
      columnName: t("vessel-call.create.table-headers.transit-passenger"),
      minWidth: 100,
    },
  ];
  const normalized = normalizePassengers(passengers);
  const rows = normalized
    .slice()
    .sort(
      (left, right) =>
        (left.no ?? Number.POSITIVE_INFINITY) - (right.no ?? Number.POSITIVE_INFINITY),
    )
    .map((p, idx) => ({
      ...p,
      id: `passenger-${idx}`,
      no: typeof p.no === "number" && Number.isFinite(p.no) ? p.no : "",
      transitPassengerLabel: p.transitPassenger ? t("common.yes") : t("common.no"),
    }));
  return {
    testIds: {
      wrapper: "review-fal-6",
      empty: "review-fal-6-empty",
    },
    translationTitle: t("vessel-call.create.fal-form-6-title"),
    headers,
    rows,
    total: passengers.length,
  };
}

export function getFal7DangerousGoodsManifestData({
  vesselCallDetails,
  vesselRegisterDetails,
  t,
}: {
  vesselCallDetails: VesselCallResponse | null;
  vesselRegisterDetails: Vessel | null;
  t: TFunction;
}) {
  const dangerousGoodsManifestUrl = vesselCallDetails?.dangerousGoodsManifestDocumentUrl || "";
  const fileName = getFalDocumentFileName(
    dangerousGoodsManifestUrl,
    "dangerousGoodsManifest",
    vesselRegisterDetails?.vesselImoNumber,
  );
  return {
    dangerousGoodsManifestUrl,
    fileName,
    translationTitle: t("vessel-call.certificates.DANGEROUS_GOODS_MANIFEST"),
    testIds: {
      wrapper: "review-fal-7",
      card: "review-fal-7-dangerous-goods-manifest-card",
      empty: "review-fal-7-no-dangerous-goods-manifest-doc",
    },
  };
}

export function normalizeCrewMembers(raw: CrewMember[] = []): NormalizedCrewMember[] {
  return raw.map((c) => ({
    ...c,
    rank: c.rank,
    placeOfBirth: c.birthPlace,
    issuingState: c.identityDocumentIssuingState,
    dateOfBirth: c.birthDate,
    expiryDate: c.identityDocumentExpiryDate,
    passportNumber: c.passportNumber,
  }));
}

export const formatEta = (eta?: string | null) => {
  if (!eta) return "-";
  const d = new Date(eta);
  if (isNaN(d.getTime())) return "-";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
};

export function normalizePassengers(raw: PassengerEntry[] = []): NormalizedPassenger[] {
  return raw.map((p) => ({
    ...p,
    familyName: p.familyName,
    givenName: p.givenName,
    nationality: p.nationality,
    dateOfBirth: p.birthDate,
    placeOfBirth: p.birthPlace,
    gender: p.gender,
    typeOfIdentityDocument: p.identityOrTravelDocumentType,
    identityDocumentNumber: p.identityOrTravelDocumentSerialNumber,
    issuingState: p.identityOrTravelDocumentIssuingState,
    expiryDate: p.identityOrTravelDocumentExpiryDate,
    visaNumberIfAppropriate: p.visaNumber,
    portOfEmbarkation: p.embarkationPort,
    portOfDisembarkation: p.disembarkationPort,
    transitPassenger: Boolean(p.transitPassenger),
  }));
}

function natureOfCallLabel({
  value,
  t,
  LANG_BASE,
}: {
  value: NatureOfCallLabelKey;
  t: TFunction;
  LANG_BASE: string;
}) {
  const translatedKey = NATURE_OF_CALL_TRANSLATION_KEY_MAP[value];
  const translated = t(`${LANG_BASE}.PreArrivalNotification.nature-call.${translatedKey}`);
  return translated;
}

export function getNatureOfCallSubSectionItems({
  vesselDetails,
  TEST_ID_BASE,
  LANG_BASE,
  t,
  transStyleClass,
  wrapperStyleClass,
  wrapperFirstStyleClass,
}: {
  vesselDetails?: VesselCallNotificationDTO | null;
  TEST_ID_BASE: string;
  LANG_BASE: string;
  t: TFunction;
  transStyleClass: string;
  wrapperStyleClass: string;
  wrapperFirstStyleClass: string;
}) {
  const natureEntries = Object.entries(vesselDetails?.natureOfCall || {}).filter(
    ([key, value]) => value === true && isNatureOfCallLabelKey(key),
  ) as Array<[NatureOfCallLabelKey, boolean]>;

  const left: SectionItem[] = [
    {
      testId: `${TEST_ID_BASE}-natureOfCall`,
      translate: t(`${LANG_BASE}.PreArrivalNotification.nature-of-call-secondary`),
      text:
        natureEntries.length === 0 ? (
          "-"
        ) : (
          <ul style={{ listStyleType: "disc" }}>
            {natureEntries.map(([key]) => (
              <li key={key}>{natureOfCallLabel({ value: key, t, LANG_BASE })}</li>
            ))}
          </ul>
        ),
      textStyles: "font-semibold",
      wrapperStyle: wrapperFirstStyleClass,
      tranStyle: transStyleClass,
    },
  ];

  if (vesselDetails?.natureOfCall?.others === true) {
    left.push({
      testId: `${TEST_ID_BASE}-otherSpecify`,
      translate: t(`${LANG_BASE}.PreArrivalNotification.other-specify`),
      text: vesselDetails.natureOfCall.otherSpecify,
      textStyles: "font-semibold",
      wrapperStyle: wrapperStyleClass,
      tranStyle: transStyleClass,
    });
  }

  if (vesselDetails?.natureOfCall?.cargoOperation === true) {
    left.push({
      testId: `${TEST_ID_BASE}-cargoOperationSpecify`,
      translate: t(`${LANG_BASE}.PreArrivalNotification.cargo-operation-specify`),
      text: vesselDetails.natureOfCall.cargoOperationSpecify,
      textStyles: "font-semibold",
      wrapperStyle: wrapperStyleClass,
      tranStyle: transStyleClass,
    });
  }

  if (vesselDetails?.natureOfCall?.wasteDisposal === true) {
    left.push({
      testId: `${TEST_ID_BASE}-wasteDisposalSpecify`,
      translate: t(`${LANG_BASE}.PreArrivalNotification.waste-disposal-specify`),
      text: vesselDetails.natureOfCall.wasteDisposalSpecify,
      textStyles: "font-semibold",
      wrapperStyle: wrapperStyleClass,
      tranStyle: transStyleClass,
    });
  }

  left.push({
    testId: `${TEST_ID_BASE}-imo2020`,
    translate: t(`${LANG_BASE}.PreArrivalNotification.method-at-IMO`),
    text:
      vesselDetails?.imo2020Compliance === true
        ? "Compliant Fuel"
        : vesselDetails?.imo2020Compliance === false
          ? "Closed Loop Exhaust Gas Scrubber"
          : "-",
    textStyles: "font-semibold",
    wrapperStyle: wrapperStyleClass,
    tranStyle: transStyleClass,
  });

  const right: SectionItem[] = [
    {
      testId: `${TEST_ID_BASE}-unitOfMeasurement`,
      translate: t(`${LANG_BASE}.PreArrivalNotification.unit-of-measurement`),
      text: vesselDetails?.unitOfMeasurement || "-",
      textStyles: "font-semibold",
      wrapperStyle: wrapperFirstStyleClass,
      tranStyle: transStyleClass,
    },
    {
      testId: `${TEST_ID_BASE}-portFacility`,
      translate: t(`${LANG_BASE}.PreArrivalNotification.port-facility`),
      text: vesselDetails?.portFacility || "-",
      textStyles: "font-semibold",
      wrapperStyle: wrapperStyleClass,
      tranStyle: transStyleClass,
    },
  ];

  if (vesselDetails?.portFacility === "OTHER") {
    right.push({
      testId: `${TEST_ID_BASE}-portFacilitySpecify`,
      translate: t(`${LANG_BASE}.PreArrivalNotification.port-facility-specify`),
      text: vesselDetails?.portFacilitySpecify || "-",
      textStyles: "font-semibold",
      wrapperStyle: wrapperStyleClass,
      tranStyle: transStyleClass,
    });
  }

  return [left, right];
}

export function getSolasIspsSubSectionItems({
  vesselDetails,
  TEST_ID_BASE,
  LANG_BASE,
  t,
  transStyleClass,
  wrapperStyleClass,
  wrapperFirstStyleClass,
}: {
  vesselDetails?: VesselCallNotificationDTO | null;
  TEST_ID_BASE: string;
  LANG_BASE: string;
  t: TFunction;
  transStyleClass: string;
  wrapperStyleClass: string;
  wrapperFirstStyleClass: string;
}) {
  const left: SectionItem[] = [
    {
      testId: `${TEST_ID_BASE}-securityLevel`,
      translate: t(`${LANG_BASE}.PreArrivalNotification.security-level`),
      text: vesselDetails?.securityLevel || "-",
      textStyles: "font-semibold",
      tranStyle: transStyleClass,
      wrapperStyle: wrapperFirstStyleClass,
    },
    {
      testId: `${TEST_ID_BASE}-isscValid`,
      translate: t(`${LANG_BASE}.PreArrivalNotification.issc-is-valid?`),
      text:
        vesselDetails?.isscValid === true
          ? t("common.yes")
          : vesselDetails?.isscValid === false
            ? t("common.no")
            : "-",
      textStyles: "font-semibold",
      tranStyle: transStyleClass,
      wrapperStyle: wrapperStyleClass,
    },
  ];

  if (vesselDetails?.isscValid) {
    left.push({
      testId: `${TEST_ID_BASE}-isscNumber`,
      translate: t(`${LANG_BASE}.PreArrivalNotification.issc-number`),
      text: vesselDetails.isscNumber || "-",
      textStyles: "font-semibold",
      tranStyle: transStyleClass,
      wrapperStyle: wrapperStyleClass,
    });
    left.push({
      testId: `${TEST_ID_BASE}-isscIssueDate`,
      translate: t(`${LANG_BASE}.PreArrivalNotification.issc-issue-date`),
      text: formatDate(vesselDetails.isscIssueDate),
      textStyles: "font-semibold",
      tranStyle: transStyleClass,
      wrapperStyle: wrapperStyleClass,
    });
    left.push({
      testId: `${TEST_ID_BASE}-isscExpireDate`,
      translate: t(`${LANG_BASE}.PreArrivalNotification.issc-expire-date`),
      text: formatDate(vesselDetails.isscExpiryDate),
      textStyles: "font-semibold",
      tranStyle: transStyleClass,
      wrapperStyle: wrapperStyleClass,
    });
  }

  if (vesselDetails?.isscIssuedBy) {
    left.push({
      testId: `${TEST_ID_BASE}-isscIssuedBy`,
      translate: t(`${LANG_BASE}.PreArrivalNotification.issued-by`),
      text: vesselDetails.isscIssuedBy,
      textStyles: "font-semibold",
      tranStyle: transStyleClass,
      wrapperStyle: wrapperStyleClass,
    });
  }

  left.push({
    testId: `${TEST_ID_BASE}-approvedSSPOnboard`,
    translate: t(`${LANG_BASE}.PreArrivalNotification.approved-ssp-onboard`),
    text:
      vesselDetails?.sspOnboardApproved === true
        ? t("common.yes")
        : vesselDetails?.sspOnboardApproved === false
          ? t("common.no")
          : "-",
    textStyles: "font-semibold",
    tranStyle: transStyleClass,
    wrapperStyle: wrapperStyleClass,
  });

  const right: SectionItem[] = [
    {
      testId: `${TEST_ID_BASE}-specialSecurityMeasures`,
      translate: t(`${LANG_BASE}.PreArrivalNotification.special-security-measures`),
      text:
        vesselDetails?.shipPortSecurityMeasuresTaken === true
          ? t("common.yes")
          : vesselDetails?.shipPortSecurityMeasuresTaken === false
            ? t("common.no")
            : "-",
      textStyles: "font-semibold",
      tranStyle: transStyleClass,
      wrapperStyle: wrapperFirstStyleClass,
    },
  ];

  if (vesselDetails?.shipPortSecurityMeasuresTaken === true) {
    right.push({
      testId: `${TEST_ID_BASE}-takenMeasures`,
      translate: t(`${LANG_BASE}.PreArrivalNotification.taken-measures`),
      text: vesselDetails?.takenMeasures || "-",
      textStyles: "font-semibold",
      tranStyle: transStyleClass,
      wrapperStyle: wrapperStyleClass,
    });
  }

  right.push({
    testId: `${TEST_ID_BASE}-securityGuardsOnBoard`,
    translate: t(`${LANG_BASE}.PreArrivalNotification.security-guards-onboard`),
    text:
      vesselDetails?.securityGuardsOnBoard === true
        ? t("common.yes")
        : vesselDetails?.securityGuardsOnBoard === false
          ? t("common.no")
          : "-",
    textStyles: "font-semibold",
    tranStyle: transStyleClass,
    wrapperStyle: wrapperStyleClass,
  });

  if (vesselDetails?.securityGuardsOnBoard === true) {
    right.push({
      testId: `${TEST_ID_BASE}-noOfGuards`,
      translate: t(`${LANG_BASE}.PreArrivalNotification.no-of-guards`),
      text:
        vesselDetails?.noOfGuards !== undefined && vesselDetails.noOfGuards !== null
          ? vesselDetails.noOfGuards.toString()
          : "-",
      textStyles: "font-semibold",
      tranStyle: transStyleClass,
      wrapperStyle: wrapperStyleClass,
    });
  }

  right.push({
    testId: `${TEST_ID_BASE}-armAmmunitionOnBoard`,
    translate: t(`${LANG_BASE}.PreArrivalNotification.arm-and-ammunition-onboard`),
    text:
      vesselDetails?.armAmmunitionOnBoard === true
        ? t("common.yes")
        : vesselDetails?.armAmmunitionOnBoard === false
          ? t("common.no")
          : "-",
    textStyles: "font-semibold",
    tranStyle: transStyleClass,
    wrapperStyle: wrapperStyleClass,
  });

  right.push({
    testId: `${TEST_ID_BASE}-vesselHasDeficiencies`,
    translate: t(`${LANG_BASE}.PreArrivalNotification.deficiencies`),
    text:
      vesselDetails?.vesselHasDeficiencies === true
        ? t("common.yes")
        : vesselDetails?.vesselHasDeficiencies === false
          ? t("common.no")
          : "-",
    textStyles: "font-semibold",
    tranStyle: transStyleClass,
    wrapperStyle: wrapperStyleClass,
  });

  if (vesselDetails?.vesselHasDeficiencies === true) {
    right.push({
      testId: `${TEST_ID_BASE}-deficienciesDetails`,
      translate: t(`${LANG_BASE}.PreArrivalNotification.deficiencies-details`),
      text: vesselDetails?.deficiencies || "-",
      textStyles: "font-semibold",
      tranStyle: transStyleClass,
      wrapperStyle: wrapperStyleClass,
    });
  }

  return [left, right];
}
